import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatAuthError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, ArrowLeft, KeyRound, Mail, CheckCircle2 } from "lucide-react";

/**
 * Two-mode page:
 *  1. Request mode (default): user enters email, we send them a Supabase
 *     recovery link that redirects back here with an auth session set.
 *  2. Update mode: onAuthStateChange fires PASSWORD_RECOVERY after the user
 *     clicks the link → we show a "pick a new password" form.
 */
export default function ResetPassword() {
  const { supabase } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("request");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") setMode("update");
    });
    return () => subscription.unsubscribe();
  }, [supabase]);

  const requestReset = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setSent(true);
    } catch (err) {
      setError(formatAuthError(err));
    } finally { setBusy(false); }
  };

  const updatePassword = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success("Password updated. Signing you in…");
      setTimeout(() => navigate("/dashboard"), 800);
    } catch (err) {
      setError(formatAuthError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background grain flex flex-col">
      <div className="max-w-7xl w-full mx-auto px-5 h-16 flex items-center">
        <Link to="/login" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-5 pb-16">
        <div className="w-full max-w-md">
          <KeyRound className="w-8 h-8 text-primary mb-3" />
          {mode === "request" ? (
            <>
              <h1 className="font-heading text-3xl tracking-tighter font-extrabold">Reset your password</h1>
              <p className="text-zinc-400 mt-2 text-sm">
                Enter the email on your account — we&apos;ll send a secure link to set a new password.
              </p>

              {sent ? (
                <div data-testid="reset-sent" className="mt-8 glass rounded-2xl p-6 flex items-start gap-3 border-emerald-400/20">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">Check your inbox</p>
                    <p className="text-sm text-zinc-400 mt-1">
                      If <span className="text-white">{email}</span> is registered with Adyap, you&apos;ll get a reset link within a minute. Don&apos;t forget the spam folder.
                    </p>
                    <button
                      onClick={() => { setSent(false); setEmail(""); }}
                      className="mt-4 text-xs text-primary hover:underline"
                    >
                      Use a different email
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={requestReset} className="mt-8 space-y-4">
                  <div>
                    <label className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Email</label>
                    <div className="relative mt-1.5">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input
                        data-testid="reset-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-3 outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                  {error && <p data-testid="reset-error" className="text-sm text-red-400">{error}</p>}
                  <button
                    data-testid="reset-submit"
                    type="submit"
                    disabled={busy}
                    className="w-full bg-primary hover:bg-primary/90 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                  >
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                    Send reset link
                  </button>
                </form>
              )}
            </>
          ) : (
            <>
              <h1 className="font-heading text-3xl tracking-tighter font-extrabold">Set a new password</h1>
              <p className="text-zinc-400 mt-2 text-sm">Pick something strong — at least 8 characters.</p>
              <form onSubmit={updatePassword} className="mt-8 space-y-4">
                <div>
                  <label className="text-xs tracking-wider uppercase font-semibold text-zinc-500">New password</label>
                  <input
                    data-testid="new-password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-primary"
                    placeholder="••••••••"
                  />
                </div>
                <div>
                  <label className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Confirm new password</label>
                  <input
                    data-testid="confirm-password"
                    type="password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-primary"
                    placeholder="••••••••"
                  />
                </div>
                {error && <p data-testid="update-error" className="text-sm text-red-400">{error}</p>}
                <button
                  data-testid="update-submit"
                  type="submit"
                  disabled={busy}
                  className="w-full bg-primary hover:bg-primary/90 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                >
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                  Update password
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
